// src/features/system/devices/components/iot/IoTQuarantinePanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Admin panel for managing the device quarantine pipeline:
//   • Top section — Pending devices awaiting approval (approve / reject)
//   • Bottom section — Blacklisted devices (unblock)
// ────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import AppDialog from '@/shared/components/ui/AppDialog';
import {
  fetchPendingDevicesApi,
  fetchBlacklistedDevicesApi,
  approveDeviceApi,
  rejectDeviceApi,
  unblockDeviceApi,
  DEVICE_KEYS,
  type PendingDevice,
  type BlacklistedDevice,
} from '../../api/iotDevicesApi';

/* ── Helpers ─────────────────────────────────────────────────────────── */

function formatTs(v: string | null): string {
  if (!v) return '—';
  try {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
  } catch { return v; }
}

function timeAgo(v: string | null): string {
  if (!v) return '—';
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return v;
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 0) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  } catch { return v; }
}

/* ── Component ───────────────────────────────────────────────────────── */

export default function IoTQuarantinePanel() {
  const qc = useQueryClient();

  /* ── Queries ────────────────────────────────────────────────── */
  const {
    data: pending = [],
    isLoading: pendingLoading,
    error: pendingError,
  } = useQuery<PendingDevice[], Error>({
    queryKey: DEVICE_KEYS.pending,
    queryFn:  fetchPendingDevicesApi,
    refetchInterval: 15_000,
  });

  const {
    data: blacklisted = [],
    isLoading: blLoading,
    error: blError,
  } = useQuery<BlacklistedDevice[], Error>({
    queryKey: DEVICE_KEYS.blacklist,
    queryFn:  fetchBlacklistedDevicesApi,
    refetchInterval: 30_000,
  });

  /* ── Approve dialog ─────────────────────────────────────────── */
  const [approveTarget, setApproveTarget] = useState<string | null>(null);
  const [approveLabel, setApproveLabel]   = useState('');

  const approveMut = useMutation({
    mutationFn: () => approveDeviceApi(approveTarget!, approveLabel || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.pending });
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.list });
      setApproveTarget(null);
      setApproveLabel('');
    },
  });

  /* ── Reject dialog ──────────────────────────────────────────── */
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectLabel, setRejectLabel]   = useState('');

  const rejectMut = useMutation({
    mutationFn: () => rejectDeviceApi(rejectTarget!, rejectReason || undefined, rejectLabel || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.pending });
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.blacklist });
      setRejectTarget(null);
      setRejectReason('');
      setRejectLabel('');
    },
  });

  /* ── Unblock dialog ─────────────────────────────────────────── */
  const [unblockTarget, setUnblockTarget] = useState<string | null>(null);

  const unblockMut = useMutation({
    mutationFn: () => unblockDeviceApi(unblockTarget!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DEVICE_KEYS.blacklist });
      setUnblockTarget(null);
    },
  });

  /* ── Pending columns ────────────────────────────────────────── */
  const pendingCols: ColumnDef<PendingDevice>[] = [
    { header: 'Device ID',  accessor: 'device_id' },
    { header: 'Scope',      accessor: 'id_scope',   Cell: (v: string) => <span className="font-mono text-xs text-neutral-400">{v}</span> },
    { header: 'First Seen', accessor: 'first_seen', Cell: (v: string | null) => <span className="text-xs cursor-default" title={formatTs(v)}>{timeAgo(v)}</span> },
    { header: 'Updated',    accessor: 'updated_at', Cell: (v: string | null) => <span className="text-xs cursor-default" title={formatTs(v)}>{timeAgo(v)}</span> },
    {
      header: 'Actions',
      accessor: 'device_id',
      Cell: (_: string, row: PendingDevice) => (
        <div className="flex gap-2">
          <button
            onClick={() => { setApproveTarget(row.device_id); setApproveLabel(''); }}
            className="px-2 py-0.5 text-xs rounded bg-emerald-700 hover:bg-emerald-600 text-white"
          >Approve</button>
          <button
            onClick={() => { setRejectTarget(row.device_id); setRejectReason(''); setRejectLabel(''); }}
            className="px-2 py-0.5 text-xs rounded bg-red-700 hover:bg-red-600 text-white"
          >Reject</button>
        </div>
      ),
    },
  ];

  /* ── Blacklist columns ──────────────────────────────────────── */
  const blCols: ColumnDef<BlacklistedDevice>[] = [
    { header: 'Device ID',   accessor: 'device_id' },
    {
      header: 'Label',
      accessor: 'label',
      Cell: (v: string | null) => v
        ? <span className="text-sky-400 text-xs">{v}</span>
        : <span className="text-neutral-600 text-xs italic">—</span>,
    },
    {
      header: 'Reason',
      accessor: 'reason',
      Cell: (v: string | null) => v
        ? <span className="text-neutral-300 text-xs">{v}</span>
        : <span className="text-neutral-600 text-xs">—</span>,
    },
    { header: 'Scope',       accessor: 'id_scope',    Cell: (v: string) => <span className="font-mono text-xs text-neutral-400">{v}</span> },
    { header: 'Rejected At', accessor: 'rejected_at', Cell: (v: string | null) => <span className="text-xs cursor-default" title={formatTs(v)}>{timeAgo(v)}</span> },
    { header: 'Updated',     accessor: 'updated_at',  Cell: (v: string | null) => <span className="text-xs cursor-default" title={formatTs(v)}>{timeAgo(v)}</span> },
    {
      header: 'Actions',
      accessor: 'device_id',
      Cell: (_: string, row: BlacklistedDevice) => (
        <button
          onClick={() => setUnblockTarget(row.device_id)}
          className="px-2 py-0.5 text-xs rounded bg-amber-700 hover:bg-amber-600 text-white"
        >Unblock</button>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col gap-6 overflow-auto">
      {/* ── Pending Devices ───────────────────────────────────── */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-200">
          Pending Devices
          {pending.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded-full bg-amber-700/40 text-amber-300">
              {pending.length}
            </span>
          )}
        </h3>
        <p className="text-xs text-neutral-500">
          Devices that have contacted the platform but are not yet authorized. Approving adds them to the registry; rejecting moves them to the blacklist.
        </p>
        {pendingError ? (
          <div className="text-red-400 text-sm">{pendingError.message}</div>
        ) : (
          <MinimalisticTable<PendingDevice>
            columns={pendingCols}
            data={pending}
            noDataMessage={pendingLoading ? 'Loading…' : 'No pending devices.'}
            showNewButton={false}
          />
        )}
      </section>

      {/* ── Blacklisted Devices ───────────────────────────────── */}
      <section className="space-y-2">
        <h3 className="text-sm font-semibold text-neutral-200">
          Blacklisted Devices
          {blacklisted.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-[10px] rounded-full bg-red-700/40 text-red-300">
              {blacklisted.length}
            </span>
          )}
        </h3>
        <p className="text-xs text-neutral-500">
          Devices that have been explicitly rejected. Unblocking removes them from the blacklist so they can be re-approved.
        </p>
        {blError ? (
          <div className="text-red-400 text-sm">{blError.message}</div>
        ) : (
          <MinimalisticTable<BlacklistedDevice>
            columns={blCols}
            data={blacklisted}
            noDataMessage={blLoading ? 'Loading…' : 'No blacklisted devices.'}
            showNewButton={false}
          />
        )}
      </section>

      {/* ── Approve dialog ────────────────────────────────────── */}
      <AppDialog
        open={!!approveTarget}
        onOpenChange={(open) => { if (!open) setApproveTarget(null); }}
        title="Approve Device"
        description={`Approve "${approveTarget}" and add it to the device registry?`}
        onConfirm={() => approveMut.mutate()}
        confirmLabel="Approve"
        isBusy={approveMut.isPending}
        size="sm"
      >
        <label className="block space-y-1">
          <span className="text-xs text-neutral-400">Label (optional)</span>
          <input
            value={approveLabel}
            onChange={e => setApproveLabel(e.target.value)}
            placeholder="e.g. Greenhouse Sensor #3"
            className="w-full px-3 py-1.5 rounded pac-glass-input
                       text-neutral-100 text-sm placeholder:text-neutral-600
                       focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </label>
        {approveMut.isError && (
          <p className="text-red-400 text-xs mt-2">{(approveMut.error as Error).message}</p>
        )}
      </AppDialog>

      {/* ── Reject dialog ─────────────────────────────────────── */}
      <AppDialog
        open={!!rejectTarget}
        onOpenChange={(open) => { if (!open) setRejectTarget(null); }}
        title="Reject Device"
        description={`Reject "${rejectTarget}" and add it to the blacklist?`}
        onConfirm={() => rejectMut.mutate()}
        confirmLabel="Reject"
        isBusy={rejectMut.isPending}
        size="sm"
      >
        <label className="block space-y-1">
          <span className="text-xs text-neutral-400">Label / Tag</span>
          <input
            value={rejectLabel}
            onChange={e => setRejectLabel(e.target.value)}
            placeholder="e.g. Suspicious MAC, Unknown origin"
            className="w-full px-3 py-1.5 rounded pac-glass-input
                       text-neutral-100 text-sm placeholder:text-neutral-600
                       focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </label>
        <label className="block space-y-1 mt-2">
          <span className="text-xs text-neutral-400">Reason (optional)</span>
          <input
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="e.g. Unknown device"
            className="w-full px-3 py-1.5 rounded pac-glass-input
                       text-neutral-100 text-sm placeholder:text-neutral-600
                       focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </label>
        {rejectMut.isError && (
          <p className="text-red-400 text-xs mt-2">{(rejectMut.error as Error).message}</p>
        )}
      </AppDialog>

      {/* ── Unblock dialog ────────────────────────────────────── */}
      <AppDialog
        open={!!unblockTarget}
        onOpenChange={(open) => { if (!open) setUnblockTarget(null); }}
        title="Unblock Device"
        description={`Remove "${unblockTarget}" from the blacklist? It will need to be re-approved before it can connect.`}
        onConfirm={() => unblockMut.mutate()}
        confirmLabel="Unblock"
        isBusy={unblockMut.isPending}
        size="sm"
      >
        {unblockMut.isError && (
          <p className="text-red-400 text-xs">{(unblockMut.error as Error).message}</p>
        )}
      </AppDialog>
    </div>
  );
}
